function _class_call_check(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _create_class(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
import stringify from "json-stable-stringify";
import { z } from "zod";
var DEFAULT_TAKE = 25;
/**
 * Utility class for working with URLs and search parameters.
 */ var UrlUtil = /*#__PURE__*/ function() {
    "use strict";
    function UrlUtil() {
        _class_call_check(this, UrlUtil);
    }
    _create_class(UrlUtil, null, [
        {
            key: "SearchParamEnum",
            value: /**
   * This is useful when you want to pass an enum as a search param. It will automatically convert
   * `all` to `undefined`. DON'T use `all` as a value in your enum.
   */ function SearchParamEnum(values) {
                return z.preprocess(function(e) {
                    if (e === "all") {
                        return undefined;
                    } else {
                        return e;
                    }
                }, z.nativeEnum(values).optional());
            }
        },
        {
            key: "SearchParamBool",
            value: function SearchParamBool() {
                return z.preprocess(function(e) {
                    if (typeof e === "boolean") {
                        return e;
                    } else if (e === "true") {
                        return true;
                    } else if (e === "false") {
                        return false;
                    } else {
                        return undefined;
                    }
                }, z.boolean().optional());
            }
        },
        {
            key: "parseSearchParams",
            value: /**
   * Parses the search parameters from the given request URL and validates them against the provided
   * schema. If the parameters match the schema, they are returned as an object.
   *
   * @template T - The zod schema type
   * @param request - The incoming request object
   * @param schema - The zod schema to validate the search parameters against
   * @returns - An object containing the parsed and validated search parameters
   */ function parseSearchParams(request, schema) {
                var searchParams = new URL(request.url).searchParams;
                var params = Object.fromEntries(searchParams.entries());
                return schema.parse(params);
            }
        },
        {
            key: "parseSearchParamsSafely",
            value: function parseSearchParamsSafely(request, schema) {
                var searchParams = new URL(request.url).searchParams;
                var params = Object.fromEntries(searchParams.entries());
                var parsedParamsRes = schema.safeParse(params);
                if (!parsedParamsRes.success) {
                    // eslint-disable-next-line no-console
                    console.warn("Unable to parse search params. Continuing anyway", parsedParamsRes.error);
                    return schema.parse({});
                } else {
                    return parsedParamsRes.data;
                }
            }
        },
        {
            key: "parseRequestSearchParamsAsObject",
            value: function parseRequestSearchParamsAsObject(request) {
                return UrlUtil.parseSearchParamsAsObject(new URL(request.url).searchParams);
            }
        },
        {
            key: "parseSearchParamsAsObject",
            value: function parseSearchParamsAsObject(searchParams) {
                // Deserialize: Convert searchParams into a form-friendly structure, handling arrays.
                var values = {};
                searchParams.forEach(function(value, key) {
                    if (values[key] === undefined) {
                        values[key] = value;
                    } else if (Array.isArray(values[key])) {
                        values[key].push(value);
                    } else {
                        values[key] = [
                            values[key],
                            value
                        ];
                    }
                });
                return values;
            }
        },
        {
            key: "parseBearerToken",
            value: /**
   * Parses the bearer token from the Authorization header of the given request. If the header is
   * present and has the correct format (Bearer <token>), the token is returned as a string.
   * Otherwise, returns null.
   *
   * @param request - The incoming request object
   * @returns - The parsed bearer token, or null if the header is missing or malformed
   */ function parseBearerToken(request) {
                var authHeader = request.headers.get("authorization");
                // Check if the header exists and has the expected format (Bearer <token>)
                if (authHeader === null || authHeader === void 0 ? void 0 : authHeader.startsWith("Bearer ")) {
                    // Extract the token from the header and return it
                    var token = authHeader.slice(7);
                    return token;
                }
                // If the header is missing or malformed, return null
                return null;
            }
        },
        {
            key: "parseRootDomain",
            value: /**
   * Parses the root domain (protocol + host) from a Request
   *
   * - @param {Request} request - The incoming request object
   *
   * @returns - The parsed bearer token, or null if the header is missing or malformed
   */ function parseRootDomain(request) {
                var url = new URL(request.url);
                return "".concat(url.protocol, "//").concat(url.host);
            }
        },
        {
            key: "parsePaginationParams",
            value: function parsePaginationParams(request) {
                var options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {
                    default: {}
                };
                var defaultOptions = options.default;
                var _defaultOptions_take;
                var defaultTake = (_defaultOptions_take = defaultOptions === null || defaultOptions === void 0 ? void 0 : defaultOptions.take) !== null && _defaultOptions_take !== void 0 ? _defaultOptions_take : DEFAULT_TAKE;
                var paginationParams = z.object({
                    skip: z.coerce.number().default(0),
                    take: z.coerce.number().default(defaultTake)
                });
                var params = paginationParams.default({
                    skip: 0,
                    take: defaultTake
                });
                return UrlUtil.parseSearchParams(request, params);
            }
        },
        {
            key: "parseSearchQuery",
            value: function parseSearchQuery(request) {
                var searchParams = z.object({
                    q: z.string().optional()
                });
                return UrlUtil.parseSearchParams(request, searchParams).q;
            }
        },
        {
            key: "makePathWithQueryObject",
            value: function makePathWithQueryObject(path, queryObject) {
                if (!queryObject) {
                    return path;
                }
                var q = UrlUtil.serializeQueryObject(queryObject);
                return "".concat(path, "?q=").concat(q);
            }
        },
        {
            key: "makePath",
            value: function makePath(path, searchParams) {
                return [
                    path,
                    searchParams.toString()
                ].filter(Boolean).join("?");
            }
        },
        {
            key: "serializeQueryObject",
            value: /**
   * Serializes an object to a base64 string.
   */ function serializeQueryObject(obj) {
                var str = stringify(obj);
                if (!str) {
                    return undefined;
                }
                return btoa(str);
            }
        },
        {
            key: "isChildPath",
            value: /**
   * Checks if one path is a child of another path. Child means it's the same path or a sub path.
   * For example:
   * - /foo is a child of /foo
   * - /foo/bar is a child of /foo
   * - /foo-bar is NOT a child of /foo
   * - /anything is a child of /
   */ function isChildPath(parentPath, childPath) {
                // Normalize paths by removing trailing slashes and ensuring leading slash
                var normalizedParent = "/" + parentPath.replace(/^\/+|\/+$/g, "");
                var normalizedChild = "/" + childPath.replace(/^\/+|\/+$/g, "");
                // Special case for root path
                if (normalizedParent === "/") {
                    return true;
                }
                // Check if paths are exactly equal
                if (normalizedParent === normalizedChild) {
                    return true;
                }
                // Check if child starts with parent path followed by a slash
                return normalizedChild.startsWith(normalizedParent + "/");
            }
        }
    ]);
    return UrlUtil;
}();
export default UrlUtil;
